import React, { useMemo } from "react";
import { View, StyleSheet } from "react-native";
import { useNavigation, type NavigationProp } from "@react-navigation/native";
import { CircleOff } from "lucide-react-native";
import {
  QuestionLayout,
  SquareOptionButton,
} from "@src/packages/medical-history/components";
import { useTranslations } from "@src/packages/i18n";
import { useSurgeries } from "../SurgeriesContext";
import type { SurgeryDetail } from "../SurgeriesContext";
import { SURGERY_OPTIONS } from "../options";
import type { SurgeryType } from "../options";
import type { SurgeriesStackParamList } from "../SurgeriesNavigator";
import { usePatient } from "@src/packages/patients/hooks/usePatient";

export function SurgeriesScreen() {
  const t = useTranslations();
  const { state, updateState } = useSurgeries();
  const navigation = useNavigation<NavigationProp<SurgeriesStackParamList>>();
  const { data: patient } = usePatient();

  const patientGender = patient?.gender as "AFAB" | "AMAB" | null | undefined;

  const filteredOptions = useMemo(
    () =>
      SURGERY_OPTIONS.filter(
        (o) => o.sex === "both" || !patientGender || o.sex === patientGender,
      ),
    [patientGender],
  );

  const selectedSurgeries = useMemo(
    () => new Set(state.surgeries.map((s) => s.surgery)),
    [state.surgeries],
  );

  const isNone = state.hasSurgeries === false;

  const toggleSurgery = (surgery: SurgeryType) => {
    if (selectedSurgeries.has(surgery)) {
      // Remove
      const toRemove = state.surgeries.find((s) => s.surgery === surgery);
      const idsToDelete = toRemove?.id
        ? [...state.surgeryIdsToDelete, toRemove.id]
        : state.surgeryIdsToDelete;
      const remaining = state.surgeries.filter((s) => s.surgery !== surgery);
      updateState({
        hasSurgeries: remaining.length > 0 ? true : null,
        surgeries: remaining,
        surgeryIdsToDelete: idsToDelete,
      });
    } else {
      // Add
      const newSurgery: SurgeryDetail = { surgery };
      updateState({
        hasSurgeries: true,
        surgeries: [...state.surgeries, newSurgery],
      });
    }
  };

  const handleNone = () => {
    const idsToDelete = state.surgeries.filter((s) => s.id).map((s) => s.id!);
    updateState({
      hasSurgeries: false,
      surgeries: [],
      surgeryIdsToDelete: [...state.surgeryIdsToDelete, ...idsToDelete],
    });
  };

  const handleSave = () => {
    if (state.hasSurgeries === true && state.surgeries.length > 0) {
      navigation.navigate("SurgeriesDetails");
    } else {
      navigation.navigate("SurgeriesComplete");
    }
  };

  const isValid =
    isNone || (state.hasSurgeries === true && state.surgeries.length > 0);

  return (
    <QuestionLayout
      title={t("med_history.medical.surgeries.title")}
      subtitle={t("med_history.medical.surgeries.subtitle")}
      onBack={() => navigation.goBack()}
      onSave={handleSave}
      saveDisabled={!isValid}
      saveLabel={t("med_history.medical.shared.next")}
    >
      <View style={styles.container}>
        <View style={styles.grid}>
          {filteredOptions.map((option) => (
            <View key={option.id} style={styles.cell}>
              <SquareOptionButton
                icon={option.icon}
                label={t(option.labelKey)}
                isSelected={!isNone && selectedSurgeries.has(option.id)}
                onPress={() => {
                  if (isNone) {
                    updateState({
                      hasSurgeries: true,
                      surgeries: [{ surgery: option.id }],
                    });
                  } else {
                    toggleSurgery(option.id);
                  }
                }}
                labelSize="sm"
              />
            </View>
          ))}
          <View style={styles.cell}>
            <SquareOptionButton
              icon={CircleOff}
              label={t("med_history.medical.surgeries.never_had_surgery")}
              isSelected={isNone}
              onPress={handleNone}
              labelSize="sm"
            />
          </View>
        </View>
      </View>
    </QuestionLayout>
  );
}

const styles = StyleSheet.create({
  container: {
    width: "100%",
    marginBottom: 40,
  },
  grid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
    width: "100%",
  },
  cell: {
    width: "47%",
  },
});
